import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faBox, faTruck, faMapMarkerAlt, faCreditCard,
  faShieldAlt, faSpinner, faCheckCircle, faTimesCircle,
  faComments, faExclamationTriangle, faUndo, faWallet, faGift
} from '@fortawesome/free-solid-svg-icons';
import { ordersAPI, paymentAPI, userAPI } from '../services/api';
import EscrowStatus from './EscrowStatus';
import '../assets/css/orderDetails.css';
import { useNotifications } from '../context/NotificationContext';

const OrderDetails = () => {
  const { showCustomToast } = useNotifications();
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [deliveryCodes, setDeliveryCodes] = useState(null);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getById(orderId);
      setOrder(response.data.order || response.data);

      // Load delivery codes if order is shipped or delivered
      const orderData = response.data.order || response.data;
      if (orderData && ['shipped', 'delivered'].includes(orderData.status)) {
        loadDeliveryCodes();
      }
    } catch (error) {
      showCustomToast({ type: 'error', message: 'Failed to load order details' });
      navigate('/buyer-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryCodes = async () => {
    try {
      const response = await ordersAPI.getDeliveryCodes(orderId);
      setDeliveryCodes(response.data || response);
    } catch (error) {
      // Don't alert - codes might not be available yet
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'confirmed': return '#3B82F6';
      case 'processing': return '#8B5CF6';
      case 'shipped': return '#10B981';
      case 'delivered': return '#22C55E';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    try {
      await ordersAPI.cancel(order.id || order._id);
      await loadOrder();
      showCustomToast({ type: 'success', message: 'Order cancelled successfully' });
    } catch (error) {
      showCustomToast({ type: 'error', message: 'Failed to cancel order' });
    }
  };

  // Step 4 per API doc: confirm delivery and pay the seller
  const handleConfirmDelivery = async () => {
    if (!window.confirm('Confirm that you have received this order? This will release payment to the seller.')) return;
    try {
      setProcessing(true);
      await ordersAPI.confirmDelivery(order.id || order._id);
      await loadOrder();
      showCustomToast({ type: 'success', message: 'Delivery confirmed! Payment has been released to the seller.' });
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error?.message || 'Failed to confirm delivery' });
    } finally {
      setProcessing(false);
    }
  };

  const handlePayNow = async () => {
    if (!order) return;
    
    try {
      setProcessing(true);
      const payment = await paymentAPI.initializePayment({
        amount: order.total_amount || order.totalAmount,
        payment_type: 'order',
        currency: 'NGN',
        callback_url: `${window.location.origin}/order-confirmation/${order.id || order._id}?payment=success`,
        order_id: order.id || order._id,
        metadata: {
          order_id: order.id || order._id,
          order_number: order.order_number || order.orderNumber
        }
      });
      
      if (payment.data?.checkout_url) {
        window.location.href = payment.data.checkout_url;
      }
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.message || 'Failed to initialize payment' });
      setProcessing(false);
    }
  };

  const handleContactSeller = async () => {
    if (!order) return;
    
    try {
      setProcessing(true);
      const sellerId = order.seller_id || order.sellerId;
      
      if (!sellerId) {
        showCustomToast({ type: 'error', message: 'Seller information not available' });
        return;
      }
      
      // Navigate to chat or create conversation
      navigate(`/chat/${sellerId}`);
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.message || 'Failed to start conversation' });
    } finally {
      setProcessing(false);
    }
  };

  const handleRunDispute = () => {
    navigate(`/disputes/create?orderId=${order.id || order._id}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="order-confirmation-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-confirmation-page">
        <div className="order-confirmation-error">
          <FontAwesomeIcon icon={faTimesCircle} size="4x" />
          <h2>Order not found</h2>
          <button onClick={() => navigate('/buyer-dashboard')} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor(order.status);
  const canCancel = ['pending', 'confirmed'].includes(order.status);
  const canReturn = order.status === 'delivered';
  // An escrow order never reaches payment_status "paid" — it goes
  // pending -> escrow -> completed (see models.PaymentStatus*). Checking
  // only "!== 'paid'" meant every escrow order (escrow is on by default)
  // showed "Pay Now" for its entire lifecycle, and clicking it would
  // initialize a brand-new Pandascrow escrow charge on top of the one
  // already funded — a real double-charge path via the UI.
  const paidStatuses = ['paid', 'escrow', 'completed'];
  const isPendingPayment = !paidStatuses.includes(order?.payment_status || order?.paymentStatus);
  const isEscrowOrder = order.payment_status === 'escrow' || order.paymentStatus === 'escrow' || order.use_escrow;
  // Shipped = FabsTech delivered to buyer, buyer must confirm to release escrow.
  // Escrow orders are excluded here: the EscrowStatus card below already
  // renders its own Confirm Delivery button for them, and that one knows to
  // collect the Pandascrow OTP first. This button calls confirm-delivery with
  // no OTP, so on an escrow order it would always fail with "OTP is required"
  // — worse, having two Confirm Delivery buttons on the same page made it easy
  // to click the wrong one and see it fail without any obvious reason why.
  const canConfirmDelivery = !isEscrowOrder && order.status === 'shipped' && (order?.payment_status || order?.paymentStatus) === 'paid';

  return (
    <div className="order-confirmation-page">
      <div className="order-confirmation-container">
        {/* Header */}
        <div className="order-details-header">
          <button onClick={() => navigate('/buyer-dashboard')} className="btn-back">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Orders
          </button>
          <h1>Order Details</h1>
        </div>

        {/* Order Status */}
        <div className="order-status-banner" style={{ background: `linear-gradient(135deg, ${statusColor}, ${statusColor}CC)` }}>
          <div className="status-content">
            <h2>{order?.status?.toUpperCase() || 'PENDING'}</h2>
            <p>Order #{order?.order_number || order?.orderNumber || 'N/A'}</p>
            <p>Placed on {order?.created_at || order?.createdAt ? new Date(order?.created_at || order?.createdAt).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>

        {order.special_packaging && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            fontSize: '0.8rem', fontWeight: 700, color: '#D97706',
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: '20px', padding: '0.35rem 0.85rem', margin: '1rem 0 0'
          }}>
            <FontAwesomeIcon icon={faGift} /> Premium Packaging
          </div>
        )}

        {(order.payment_status === 'escrow' || order.paymentStatus === 'escrow' || order.use_escrow) && (
          <EscrowStatus orderId={order.id || order._id} userRole="buyer" onStatusChange={loadOrder} />
        )}

        {/* Order Items */}
        <div className="order-section">
          <h3><FontAwesomeIcon icon={faBox} /> Items Ordered</h3>
          <div className="order-items">
            {order.items?.map((item, index) => (
              <div key={index} className="order-item">
                <img 
                  src={item?.product_image || item?.product?.images?.[0]?.url || 'https://via.placeholder.com/80'} 
                  alt={item?.product_title || item?.product?.title || 'Product'}
                />
                <div className="order-item-info">
                  <h4>{item?.product_title || item?.product?.title || 'Unknown Product'}</h4>
                  <p>Condition: {item?.product?.condition || 'N/A'}</p>
                  <div className="item-pricing">
                    <span className="item-price">{formatPrice(item?.unit_price || item?.price || 0)}</span>
                    <span className="item-quantity">Qty: {item?.quantity || 0}</span>
                  </div>
                </div>
                <div className="item-total">
                  {formatPrice((item?.unit_price || item?.price || 0) * (item?.quantity || 0))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Information */}
        <div className="order-section">
          <h3><FontAwesomeIcon icon={faMapMarkerAlt} /> Delivery Information</h3>
          <div className="delivery-info">
            <div className="address-section">
              <h4>Delivery Address</h4>
              <p>
                {order?.shipping_address?.street || order?.shippingAddress?.street},
                {order?.shipping_address?.city || order?.shippingAddress?.city},
                {order?.shipping_address?.state || order?.shippingAddress?.state}
              </p>
            </div>
            {(order?.tracking_number || order?.trackingInfo?.trackingNumber) && (
              <div className="tracking-section">
                <h4>Tracking Number</h4>
                <p className="tracking-number">
                  {order?.tracking_number || order?.trackingInfo?.trackingNumber}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Verification Codes */}
        {deliveryCodes && (order.status === 'shipped' || order.status === 'delivered') && (
          <div className="order-section">
            <h3><FontAwesomeIcon icon={faShieldAlt} /> Delivery Verification Codes</h3>
            <div className="delivery-codes-section" style={{
              display: 'grid',
              gridTemplateColumns: deliveryCodes.pickup_code && deliveryCodes.delivery_code ? '1fr 1fr' : '1fr',
              gap: '1.5rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              {deliveryCodes.pickup_code && (
                <div className="verification-code-card" style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Pickup Code
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#22C55E',
                    letterSpacing: '0.1em',
                    fontFamily: "'Geist', monospace",
                    marginBottom: '0.5rem'
                  }}>
                    {deliveryCodes.pickup_code}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF'
                  }}>
                    Show this code to the courier when they pickup your item
                  </div>
                </div>
              )}
              {deliveryCodes.delivery_code && (
                <div className="verification-code-card" style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Delivery Code
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#22C55E',
                    letterSpacing: '0.1em',
                    fontFamily: "'Geist', monospace",
                    marginBottom: '0.5rem'
                  }}>
                    {deliveryCodes.delivery_code}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF'
                  }}>
                    {order.status === 'delivered'
                      ? 'Verified on delivery'
                      : 'Provide this code to receive your item'}
                  </div>
                  {deliveryCodes.code_verified_at && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      color: '#16A34A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Verified on {new Date(deliveryCodes.code_verified_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              fontSize: '0.875rem',
              color: '#3B82F6'
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
              <strong>Important:</strong> Keep these codes secure. They will be used to verify the pickup and delivery of your order.
            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="order-section order-summary">
          <h3>Order Summary</h3>
          <div className="summary-rows">
            <div className="summary-row">
              <span>Subtotal ({order?.items?.length || 0} items)</span>
              <span>{formatPrice(order?.subtotal_amount || order?.subtotal || 0)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {(order?.shipping_amount || order?.shippingCost || 0) === 0 
                  ? 'FREE' 
                  : formatPrice(order?.shipping_amount || order?.shippingCost || 0)}
              </span>
            </div>
            {(order?.discount_amount || order?.discount || 0) > 0 && (
              <div className="summary-row">
                <span>Discount</span>
                <span className="discount">-{formatPrice(order?.discount_amount || order?.discount || 0)}</span>
              </div>
            )}
            <div className="summary-divider"></div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatPrice(order?.total_amount || order?.totalAmount || 0)}</span>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="order-section">
          <h3><FontAwesomeIcon icon={faCreditCard} /> Payment Information</h3>
          <div className="payment-info">
            <div className="payment-row">
              <span>Payment Method:</span>
              <strong>
                {typeof (order?.payment_method || order?.paymentMethod) === 'object'
                  ? `**** ${(order?.payment_method || order?.paymentMethod)?.last4}` 
                  : (order?.payment_method || order?.paymentMethod || 'Payment method')}
              </strong>
            </div>
            <div className="payment-row">
              <span>Payment Status:</span>
              <strong className={`payment-status ${(order?.payment_status || order?.paymentStatus) === 'paid' ? 'paid' : 'pending'}`}>
                {(order?.payment_status || order?.paymentStatus) === 'paid' ? 'Paid' : 'Pending Payment'}
              </strong>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="order-actions">
          {canConfirmDelivery && (
            <button
              onClick={handleConfirmDelivery}
              disabled={processing}
              className="btn-primary"
              style={{ background: '#10b981', borderColor: '#10b981' }}
            >
              {processing ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
              ) : (
                <><FontAwesomeIcon icon={faCheckCircle} /> Confirm Delivery</>
              )}
            </button>
          )}

          {isPendingPayment && (
            <button 
              onClick={handlePayNow} 
              disabled={processing}
              className="btn-primary"
            >
              {processing ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
              ) : (
                <><FontAwesomeIcon icon={faWallet} /> Pay Now</>
              )}
            </button>
          )}
          
          {canReturn && (
            <button onClick={() => navigate(`/orders/${order.id || order._id}/return`)} className="btn-secondary">
              <FontAwesomeIcon icon={faUndo} /> Request Return
            </button>
          )}
          
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <button onClick={handleRunDispute} className="btn-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} /> Run Dispute
            </button>
          )}
          
          {canCancel && (
            <button onClick={handleCancelOrder} className="btn-danger">
              <FontAwesomeIcon icon={faTimesCircle} /> Cancel Order
            </button>
          )}
          
          <button onClick={handleContactSeller} className="btn-primary">
            <FontAwesomeIcon icon={faComments} /> Contact Seller
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
